import { safeJson } from "src/app/helpers/Index";
import { faCalendarStar, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React from "react";
import { EventService, FileService, SeasonService, SponsorService, VenueService } from "src/app/services/Index";
import { ResizeImage } from "src/app/helpers/ResizeImage";
import { FileUpload } from "primereact/fileupload";
import { environment } from "src/environments/environment";
import { TicketingService } from "src/app/services/TicketingService";
import { ScheduleHero, ScheduleHeroEmpty } from "src/app/components/ScheduleHero";
import "./AdminEvents.css";
import "../ticketing/AdminTicketing.css";

const eventMoney = (pence: any) =>
	`£${((Number(pence) || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type Props = {};
type State = {
	available: boolean;
	isLoading: boolean;
	saving: boolean;
	events: any[];
	seasons: any[];
	venues: any[];
	sponsors: any[];
	summaries: { [eventId: number]: any };
	showDialog: boolean;
	edit: any;
};

const AMENITIES: { key: string; label: string }[] = [
	{ key: "hasFoodAndDrink", label: "Food and drink" },
	{ key: "hasMerchandise", label: "Merchandise" },
	{ key: "hasParking", label: "Parking" },
	{ key: "hasAccessibleEntrances", label: "Accessible entrances" },
	{ key: "hasAccessibleToilets", label: "Accessible toilets" },
	{ key: "hasCashMachine", label: "Cash machine" },
	{ key: "hasInternet", label: "Internet" }
];

const when = (event: any) => {
	const start = new Date(event.startDateTime);
	const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", weekday: "short", year: "numeric" };
	const time = start.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
	if (!event.endDateTime) return `${start.toLocaleDateString("en-GB", opts)} · ${time}`;
	const end = new Date(event.endDateTime);
	return `${start.toLocaleDateString("en-GB", opts)} – ${end.toLocaleDateString("en-GB", opts)}`;
};

/**
 * Non-matchday events — the nights a club sells between fixtures.
 *
 * Past events stay in the list rather than being hidden: a club looks at last
 * month's takings as often as next month's listing. They're just dimmed.
 */
export class AdminEvents extends React.Component<Props, State> {
	toast: any;

	constructor(props: Props) {
		super(props);
		this.state = {
			available: true, isLoading: true, saving: false, venues: [], sponsors: [], summaries: {},
			events: [], seasons: [], showDialog: false, edit: null
		};
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		try {
			const status = await safeJson(EventService.status());
			if (status?.available === false) {
				this.setState({ available: false, isLoading: false });
				return;
			}
			const [events, seasons] = await Promise.all([
				(await EventService.getAll()).json(),
				(await SeasonService.getAll()).json()
			]);
			this.setState({
				events: Array.isArray(events) ? events : [],
				seasons: Array.isArray(seasons) ? seasons : [],
				isLoading: false
			});

			this.loadVenues();
			this.loadSponsors();
			this.loadSummaries();
		} catch {
			this.setState({ available: false, isLoading: false });
		}
	}


	/** Venue names, so the list can show where rather than an id. */
	async loadVenues() {
		try {
			const res = await VenueService.getAll();
			if (!res.ok || res.status === 204) return;
			const body = await res.json();
			this.setState({ venues: Array.isArray(body) ? body : [] });
		} catch { /* the column just reads em-dash */ }
	}

	async loadSponsors() {
		try {
			const res = await SponsorService.getAll();
			if (!res.ok || res.status === 204) return;
			const body = await res.json();
			this.setState({ sponsors: Array.isArray(body) ? body : [] });
		} catch { /* the dropdown just has no options */ }
	}

	/** Sales figures for every event in one request. */
	async loadSummaries() {
		try {
			const res = await TicketingService.getEventSummaries();
			if (!res.ok || res.status === 204) return;
			const body = await res.json();
			if (!body?.available || !Array.isArray(body.events)) return;
			const summaries: any = {};
			for (const row of body.events) summaries[row.eventId] = row;
			this.setState({ summaries });
		} catch { /* no sales columns rather than no list */ }
	}

	venueName(event: any) {
		return this.state.venues.find((v: any) => v.id === event.venueId)?.name || "\u2014";
	}

	/** The event just gone and the one coming up. */
	heroEvents() {
		const all = [...(this.state.events || [])].sort(
			(a: any, b: any) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime());
		const now = Date.now();
		const past = all.filter((e: any) => new Date(e.startDateTime).getTime() < now);
		const future = all.filter((e: any) => new Date(e.startDateTime).getTime() >= now);
		return { previous: past[past.length - 1] || null, next: future[0] || null };
	}

	eventStrip(event: any) {
		const when = new Date(event.startDateTime);
		return [
			when.toLocaleDateString("en-GB", { day: "numeric", month: "short", weekday: "short", year: "2-digit" }),
			when.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
			this.state.venues.find((v: any) => v.id === event.venueId)?.name
		].filter(Boolean).join(" | ");
	}

	/**
	 * Whether a fan can buy right now.
	 *
	 * Events have no on-sale flag of their own — their sale windows decide it —
	 * so an event with none set reads as not on sale, which it is.
	 */
	saleDot(status: string | undefined) {
		const known: { [k: string]: { tone: string; label: string } } = {
			onsale: { tone: "green", label: "On sale" },
			scheduled: { tone: "amber", label: "Priority sales" }
		};
		const it = known[status || ""] || { tone: "red", label: "Not on sale" };
		return <span aria-label={it.label} className={`tk-dot ${it.tone}`} role="img" title={it.label}><i /></span>;
	}


	/**
	 * Upload artwork and hang it on the event being edited.
	 *
	 * Resized before upload — a club will happily attach a 12MP photo from a
	 * phone, and every fan then downloads it to render a 54px logo.
	 */
	async uploadImage(data: any, property: string) {
		try {
			const form = new FormData();
			form.append("containerName", "uploads");
			form.append("files", await ResizeImage(data.files[0]));
			form.append("mimeTypeKey", "Images");

			const response = await safeJson(FileService.upload(form));
			const url = `${environment.server.storageUrl}/${response[0].location}`;
			this.setState({ edit: { ...this.state.edit, [property]: url } });
		} catch {
			this.toast?.show({ closable: false, detail: "That image didn't upload. Please try again.", life: 4000, severity: "error", summary: "Upload" });
		}
	}

	renderImageField(label: string, property: string, hint: string) {
		const value = this.state.edit?.[property];
		return (
			<div className="ev-image">
				<label className="ev-label">{label}</label>
				{value && <div className="ev-image-preview">
					<img alt="" src={value} />
					<Button className="p-button-text p-button-danger p-button-sm" label="Remove"
						onClick={() => this.setState({ edit: { ...this.state.edit, [property]: null } })} />
				</div>}
				<FileUpload accept="image/*" auto chooseLabel={value ? "Replace" : "Upload"} customUpload mode="basic"
					uploadHandler={(e) => { this.uploadImage(e, property); (e.options as any)?.clear?.(); }} />
				<small className="ev-hint">{hint}</small>
			</div>
		);
	}

	renderHeroes() {
		const { previous, next } = this.heroEvents();
		return (
			<div className="sh-row">
				{previous
					? <ScheduleHero imageUrl={previous.logoUrl} label="Previous Event" onView={() => this.open(previous)} strip={this.eventStrip(previous)} title={previous.name} />
					: <ScheduleHeroEmpty label="Previous Event" message="Nothing has been held yet." />}
				{next
					? <ScheduleHero imageUrl={next.logoUrl} label="Next Event" onView={() => this.open(next)} strip={this.eventStrip(next)} title={next.name} />
					: <ScheduleHeroEmpty label="Next Event" message="Nothing scheduled." />}
			</div>
		);
	}

	open(event?: any) {
		if (event) {
			const flat: any = { ...event, ...(event.amenities || {}) };
			// The API nests amenities for reading; the form wants them flat.
			for (const a of AMENITIES) flat[a.key] = !!(event.amenities || {})[a.key.replace(/^has/, "").replace(/^./, (c: string) => c.toLowerCase())];
			flat.startDateTime = new Date(event.startDateTime);
			flat.endDateTime = event.endDateTime ? new Date(event.endDateTime) : null;
			flat.hasEndDate = !!event.endDateTime;
			this.setState({ edit: flat, showDialog: true });
			return;
		}

		const start = new Date();
		start.setHours(19, 0, 0, 0);
		this.setState({
			edit: { hasEndDate: false, name: "", showOnSchedule: true, startDateTime: start },
			showDialog: true
		});
	}

	async save() {
		const e = this.state.edit;
		if (!e?.name?.trim()) return;
		if (e.hasEndDate && e.endDateTime && new Date(e.endDateTime) <= new Date(e.startDateTime)) {
			this.toast.show({ closable: false, detail: "It has to end after it starts.", life: 4000, severity: "warn", summary: "Check the dates" });
			return;
		}

		this.setState({ saving: true });
		const payload: any = {
			contentSponsorId: e.contentSponsorId || null,
			description: e.description || null,
			endDateTime: e.hasEndDate ? e.endDateTime : null,
			hideTeamCrest: !!e.hideTeamCrest,
			name: e.name.trim(),
			seasonId: e.seasonId || null,
			logoUrl: e.logoUrl || null,
			backgroundUrl: e.backgroundUrl || null,
			showOnSchedule: !!e.showOnSchedule,
			startDateTime: e.startDateTime,
			streamingUrl: e.streamingUrl || null,
			ticketTemplateId: e.ticketTemplateId || null,
			venueId: e.venueId || null
		};
		for (const a of AMENITIES) payload[a.key] = !!e[a.key];

		const res = e.id ? await EventService.update(e.id, payload) : await EventService.add(payload);
		this.setState({ saving: false });

		if (!res.ok) {
			const message = await res.text();
			this.toast.show({ closable: false, detail: message || "Please try again later.", life: 5000, severity: "error", summary: "Couldn't save" });
			return;
		}
		this.setState({ edit: null, showDialog: false });
		this.load();
	}

	async remove(event: any) {
		if (!window.confirm(`Remove "${event.name}"? Tickets already sold to it stay valid.`)) return;
		await EventService.remove(event.id);
		this.load();
	}

	render() {
		const { available, isLoading, events, seasons } = this.state;
		const edit = this.state.edit;

		if (!isLoading && !available) {
			return (
				<div style={{ padding: 20 }}>
					<h2 style={{ marginBottom: 20 }}>Events</h2>
					<div className="ev-empty">
						<FontAwesomeIcon icon={faCalendarStar} />
						<h3>Events aren&rsquo;t switched on yet</h3>
						<p>The event tables haven&rsquo;t been created on this environment.</p>
					</div>
				</div>
			);
		}

		return (
			<div style={{ padding: 20 }}>
				<Toast ref={(el) => (this.toast = el)} />
				<h2 style={{ marginBottom: 6 }}>Events</h2>
				<p style={{ color: "var(--pb-text-muted)", marginBottom: 20 }}>
					Nights that aren&rsquo;t matches &mdash; dinners, quiz nights, legends games, open days. They sell exactly like a fixture.
				</p>

				<div style={{ marginBottom: 18 }}>
					<Button icon="pi pi-plus" label="Add event" onClick={() => this.open()} />
				</div>

				{!isLoading && this.renderHeroes()}

				{isLoading && <p style={{ color: "var(--pb-text-muted)" }}>Loading&hellip;</p>}

				{!isLoading && events.length === 0 &&
					<div className="ev-empty">
						<FontAwesomeIcon icon={faCalendarStar} />
						<h3>No events yet</h3>
						<p>Add one and it can be priced, put on sale and sold through the same ticketing as your fixtures.</p>
					</div>}

				{!isLoading && events.length > 0 && <div className="tk-table-wrap">
					<table className="tk-table">
						<thead>
							<tr>
								<th className="dot"></th>
								<th>Event name</th>
								<th>Venue</th>
								<th>Date</th>
								<th>Time</th>
								<th className="num">General</th>
								<th className="num">Hospitality</th>
								<th className="num">Total revenue</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{events.map((event: any) => {
								const start = new Date(event.startDateTime);
								const sum = this.state.summaries[event.id];
								return (
									<tr className={event.isPast ? "past" : ""} key={`e-${event.id}`}>
										<td className="dot">{this.saleDot(sum?.saleStatus)}</td>
										<td>
											<div className="ev-name">
												{event.logoUrl && <img alt="" className="ev-row-logo" src={event.logoUrl} />}
												<span>{event.name}</span>
												{event.isPast && <span className="ev-badge">PAST</span>}
												{!event.showOnSchedule && <span className="ev-badge">HIDDEN</span>}
											</div>
										</td>
										<td>{this.venueName(event)}</td>
										<td>{start.toLocaleDateString("en-GB")}</td>
										<td>{start.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</td>
										<td className="num">{sum?.generalTickets ?? 0}</td>
										<td className="num">{sum?.hospitalityTickets ?? 0}</td>
										<td className="num">{eventMoney(sum?.revenuePence ?? 0)}</td>
										<td>
											<div className="ev-row-actions">
												<Button className="p-button-sm" label="View" onClick={() => this.open(event)} />
												<Button className="p-button-text p-button-danger" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => this.remove(event)} style={{ padding: "4px 8px" }} />
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>}

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showDialog: false })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.save()} />
				</>} header={edit?.id ? "Edit event" : "New event"} modal onHide={() => this.setState({ showDialog: false })} style={{ width: 560 }} visible={this.state.showDialog}>
					{edit && <>
						<label className="ev-label">Name</label>
						<InputText autoFocus onChange={(e) => this.setState({ edit: { ...edit, name: e.target.value } })} placeholder="An Evening With…" style={{ marginBottom: 16, width: "100%" }} value={edit.name || ""} />

						<div className="ev-two">
							<div>
								<label className="ev-label">Starts</label>
								<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.setState({ edit: { ...edit, startDateTime: e.value } })} showTime style={{ width: "100%" }} value={edit.startDateTime} />
							</div>
							<div>
								<label className="ev-label">Season</label>
								<select className="ev-select" onChange={(e) => this.setState({ edit: { ...edit, seasonId: e.target.value ? Number(e.target.value) : null } })} value={edit.seasonId ?? ""}>
									<option value="">Current season</option>
									{seasons.map((s) => <option key={`es-${s.id}`} value={s.id}>{s.name}</option>)}
								</select>
							</div>
						</div>

						<div className="ev-switch" style={{ marginTop: 14 }}>
							<InputSwitch checked={!!edit.hasEndDate} onChange={(e) => this.setState({ edit: { ...edit, hasEndDate: e.value } })} />
							<label>Runs over more than one day</label>
						</div>
						{edit.hasEndDate &&
							<div style={{ marginTop: 10 }}>
								<label className="ev-label">Ends</label>
								<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.setState({ edit: { ...edit, endDateTime: e.value } })} showTime style={{ width: "100%" }} value={edit.endDateTime} />
							</div>}

						<div className="ev-two" style={{ marginTop: 16 }}>
							{this.renderImageField("Event logo", "logoUrl", "Shown in lists and on the hero card. A square works best.")}
							{this.renderImageField("Background image", "backgroundUrl", "Dresses the event's storefront page. Wide, not square.")}
						</div>

						<label className="ev-label" style={{ marginTop: 16 }}>Description</label>
						<InputTextarea onChange={(e) => this.setState({ edit: { ...edit, description: e.target.value } })} rows={3} style={{ width: "100%" }} value={edit.description || ""} />

						<label className="ev-label" style={{ marginTop: 16 }}>What&rsquo;s on offer</label>
						<div className="ev-amenities">
							{AMENITIES.map((a) => (
								<label className={`ev-amenity${edit[a.key] ? " on" : ""}`} key={a.key}>
									<input checked={!!edit[a.key]} onChange={(e) => this.setState({ edit: { ...edit, [a.key]: e.target.checked } })} type="checkbox" />
									{a.label}
								</label>
							))}
						</div>
						<p className="ev-hint">Shown on the listing. Answering here saves the phone calls.</p>

						<label className="ev-label" style={{ marginTop: 16 }}>Sponsor</label>
						<select className="ev-select" onChange={(e) => this.setState({ edit: { ...edit, contentSponsorId: e.target.value ? Number(e.target.value) : null } })} value={edit.contentSponsorId ?? ""}>
							<option value="">No sponsor</option>
							{this.state.sponsors.map((sponsor: any) => (
								<option key={`sp-${sponsor.id}`} value={sponsor.id}>{sponsor.name}</option>
							))}
						</select>

						<label className="ev-label" style={{ marginTop: 16 }}>Streaming link (optional)</label>
						<InputText onChange={(e) => this.setState({ edit: { ...edit, streamingUrl: e.target.value } })} style={{ width: "100%" }} value={edit.streamingUrl || ""} />

						<div className="ev-switch" style={{ marginTop: 18 }}>
							<InputSwitch checked={!!edit.showOnSchedule} onChange={(e) => this.setState({ edit: { ...edit, showOnSchedule: e.value } })} />
							<label>Show on the public schedule</label>
						</div>

						<div className="ev-switch" style={{ marginTop: 12 }}>
							<InputSwitch checked={!!edit.hideTeamCrest} onChange={(e) => this.setState({ edit: { ...edit, hideTeamCrest: e.value } })} />
							<label>Hide the club crest</label>
						</div>
						<p className="ev-hint">Some nights aren&rsquo;t really football and don&rsquo;t want the badge on them.</p>
					</>}
				</Dialog>
			</div>
		);
	}
}
